import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { calculateCablePrice, formatPrice } from '../services/pricingService';
import { cableTypes } from './CableTypeSelection';
import {
  generateSKU,
  skuCacheService,
  forceGenerateSKU,
  resolveLengthFeetForImage,
  isStandardEndToEndStyle
} from '../services/skuService';
import { findConnectorWithSkuCode } from '../services/utilityService';
import { compatibilityMatrix } from '../services/compatibilityMatrix';
import { getWireTypeDescription } from '../services/wireCompatibilityService';
import { downloadCableSVG, downloadCableDiagram } from '../services/diagramUploadService';
import { API_ENDPOINTS } from '../config';
import { cableImageCaptureService } from '../services/cableImageCaptureService';
import { generateUnifiedCableSVG } from '../services/unifiedSvgGenerator';
import { canGenerateSVG } from '../services/customerSvgExportService';
import { canGenerateBOM } from '../services/bomGeneratorService';
import BOMDialog from './BOMDialog';
import { getCableColor, getCableThickness } from '../services/svgConnectorGenerator';
import { generateEndToEndImage, captureCableConfiguration } from '../services/localSvgGenerator';
import { hasAnyCustomHeatShrinkEnd, isCustomHeatShrinkArtworkComplete } from '../utils/customHeatShrinkHelpers';
import { buildFinishingDisplayRows } from '../utils/finishingDisplayRows';
import FinishingDetailRows from './FinishingDetailRows';
import {
  closeCartTab,
  navigateCartTab,
  openCartTabDuringGesture
} from '../utils/connectorDisplayUtils';


// Helper function to check if connector is XLR 5-pin
function isXLR5Pin(connector) {
  return connector?.id === 'NC5FXX' || connector?.id === 'NC5MXX';
}

// Helper function to get the correct wire type for display (same logic as BOM generator)
function getCorrectWireTypeForDisplay(config) {
  // Handle different cable styles
  if (config.style === 'zip' || config.style === 'zip-audio-audio') {
    // Check if this is actually a hybrid cable with wrong style
    if (config.type === 'hybrid') {
      // This is a hybrid cable with wrong style - use PSH
      return config.audioWireType || 'PSH';
    } else {
      // True zip cables use PSZ
      return 'PSZ';
    }
  } else if (config.style === 'wye') {
    return 'PSZ'; // Wye cables always use PSZ (Zip cable type)
  } else if (config.style === 'hybrid' || config.style === 'hybrid-power-audio-audio') {
    // Hybrid cables always use PSH, but check config first for consistency
    return config.audioWireType || 'PSH'; // Hybrid cables always use PSH
  } else if (config.type === 'power') {
    // Power cables use PSP + wire gauge
    const wireGauge = config.wireGauge?.gauge || config.powerWireGauge;
    if (wireGauge) {
      const awg = wireGauge.replace('AWG', ''); // Remove 'AWG' suffix if present
      return `PSP${awg}`;
    } else {
      return 'PSP'; // Default power cable without specific gauge
    }
  } else if (config.type === 'speaker') {
    // Speaker cables use PSS + wire gauge
    const wireGauge = config.wireGauge?.gauge || config.speakerWireGauge;
    if (wireGauge) {
      const awg = wireGauge.replace('AWG', ''); // Remove 'AWG' suffix if present
      return `PSS${awg}`;
    } else {
      // Use the wire type from config
      return config.wireType || 'PSS16';
    }
  } else {
    // For standard audio cables, check if this is a speaker or power cable based on wire type
    // If wire type starts with PSS, treat it as a speaker cable
    if (config.wireType && config.wireType.startsWith('PSS')) {
      return config.wireType; // Use PSS14, PSS12, PSS16 directly
    } else if (config.wireType && config.wireType.startsWith('PSP')) {
      return config.wireType; // Use PSP14, PSP12, PSP16 directly
    } else {
      // For other audio cables, use the wire type from config
      return config.wireType || config.audioWireType || 'PSM';
    }
  }
}



// Helper function to get connector display name with short SKU
function getConnectorDisplayName(connector) {
  if (!connector) return '';
  
  // Get the short SKU code
  const skuCode = findConnectorWithSkuCode(connector);
  
  // Get the proper description from the compatibility matrix
  let baseName = '';
  if (connector?.id) {
    const matrixEntry = compatibilityMatrix[connector.id];
    if (matrixEntry?.description) {
      baseName = matrixEntry.description;
    } else if (connector.description) {
      baseName = connector.description;
    } else if (connector.label) {
      baseName = connector.label;
    } else if (connector.connector) {
      baseName = connector.connector;
    } else {
      baseName = connector.id || '';
    }
  } else {
    // Fallback for legacy connector format
    baseName = connector.label || connector.description || connector.connector || '';
  }
  
  // If we have a short SKU code and it's different from the base name, include both
  if (skuCode && skuCode !== 'UNKNOWN' && skuCode !== baseName) {
    return `${baseName} (${skuCode})`;
  }
  
  return baseName;
}

function ReviewCart({ config, cart, onAddToCart, onRemoveFromCart, svgRef = null, isEmbedded = false }) {

  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);
  const [bomDialogOpen, setBomDialogOpen] = useState(false);
  const [bigCommerceLoading, setBigCommerceLoading] = useState(false);
  const [bigCommerceMessage, setBigCommerceMessage] = useState('');
  const [bigCommerceSuccess, setBigCommerceSuccess] = useState(false);
  const [cartUrl, setCartUrl] = useState('');
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [productUrl, setProductUrl] = useState('');
  const [cachedSku, setCachedSku] = useState(null);

  // Debug logging for special instructions
  useEffect(() => {
    console.log('🔍 ReviewCart: Config received:', {
      config: config,
      configSpecialInstructions: config?.specialInstructions,
      configHybridConnectors: config?.hybridConnectors,
      hybridConnectorsSpecialInstructions: config?.hybridConnectors?.specialInstructions,
      configKeys: config ? Object.keys(config) : []
    });
  }, [config]);
  

  
  // Use useMemo to prevent these objects from changing on every render
  const endA = useMemo(() => 
    config.connectors?.endA || config.endA || { brand: '', connector: '', gender: '', variant: '' },
    [config.connectors?.endA, config.endA]
  );
  
  const endB = useMemo(() => 
    config.connectors?.endB || config.endB || { brand: '', connector: '', gender: '', variant: '' },
    [config.connectors?.endB, config.endB]
  );
  
  const endC = useMemo(() => 
    config.connectors?.endC || config.endC || {},
    [config.connectors?.endC, config.endC]
  );
  
  const hybridConnectors = useMemo(() => 
    config.hybridConnectors || {},
    [config.hybridConnectors]
  );
  
  // Determine required connectors based on style
  let missingConnector = false;
  let missingCableType = !config.type;
  
  if (config.style === 'wye') {
    // For wye cables, check if connectors E, F, G exist in hybridConnectors (updated from D, E, F)
    const hybridConnectorsObj = hybridConnectors.connectors || hybridConnectors || {};
    missingConnector = !hybridConnectorsObj.E?.id || !hybridConnectorsObj.F?.id || !hybridConnectorsObj.G?.id;
  } else if (config.style === 'zip' || config.style === 'hybrid' || config.type === 'hybrid') {
    // Both zip and hybrid now require G, H, I, J (updated from F, G, H, I)
    const hybridConnectorsObj = hybridConnectors.connectors || hybridConnectors || {};
    missingConnector = !hybridConnectorsObj.G?.id || !hybridConnectorsObj.H?.id || !hybridConnectorsObj.I?.id || !hybridConnectorsObj.J?.id;
  } else if (config.style === 'zip-audio-audio') {
    // Zip audio+audio requires G, H, I, J
    const hybridConnectorsObj = hybridConnectors.connectors || hybridConnectors || {};
    missingConnector = !hybridConnectorsObj.G?.id || !hybridConnectorsObj.H?.id || !hybridConnectorsObj.I?.id || !hybridConnectorsObj.J?.id;
  } else if (config.style === 'hybrid-power-audio-audio') {
    // Hybrid power+audio+audio requires I, J, K, L, M, N
    const hybridConnectorsObj = hybridConnectors.connectors || hybridConnectors || {};
    
    // Debug: Check if we're getting the right data structure
    console.log('ReviewCart: ALWAYS LOG - Data structure check:', {
      hybridConnectors: hybridConnectors,
      hybridConnectorsType: typeof hybridConnectors,
      hasConnectorsProperty: hybridConnectors && typeof hybridConnectors === 'object' && 'connectors' in hybridConnectors,
      hybridConnectorsObj: hybridConnectorsObj,
      hybridConnectorsObjType: typeof hybridConnectorsObj,
      hybridConnectorsObjKeys: Object.keys(hybridConnectorsObj)
    });
    
    // Additional check to ensure we have a valid config before calculating missingConnector
    if (!hybridConnectors || !hybridConnectorsObj || Object.keys(hybridConnectorsObj).length === 0) {
      console.log('ReviewCart: ALWAYS LOG - Config not ready yet, skipping missingConnector calculation');
      missingConnector = true; // Still missing if config isn't ready
    } else {
    
    // ALWAYS log the full config structure to understand what we're working with
    console.log('ReviewCart: ALWAYS LOG - Full config structure:', {
      config: config,
      hybridConnectors: hybridConnectors,
      hybridConnectorsObj: hybridConnectorsObj,
      hybridConnectorsObjKeys: Object.keys(hybridConnectorsObj)
    });
    
    // ALWAYS log each connector object individually
    Object.entries(hybridConnectorsObj).forEach(([key, connector]) => {
      console.log(`ReviewCart: ALWAYS LOG - Connector ${key}:`, {
        connector: connector,
        hasId: !!connector?.id,
        id: connector?.id,
        type: connector?.type,
        label: connector?.label,
        skuCode: connector?.skuCode,
        purpose: connector?.purpose,
        family: connector?.family,
        gender: connector?.gender,
        configuration: connector?.configuration,
        keys: connector ? Object.keys(connector) : 'null/undefined'
      });
    });
    
    // Debug logging to help identify the issue
    if (process.env.NODE_ENV === 'development') {
      console.log('ReviewCart: Checking hybrid-power-audio-audio connectors:', {
        I: hybridConnectorsObj.I,
        J: hybridConnectorsObj.J,
        K: hybridConnectorsObj.K,
        L: hybridConnectorsObj.L,
        M: hybridConnectorsObj.M,
        N: hybridConnectorsObj.N
      });
      console.log('ReviewCart: hybridConnectorsObj keys:', Object.keys(hybridConnectorsObj));
      console.log('ReviewCart: hybridConnectorsObj.J?.id:', hybridConnectorsObj.J?.id);
      console.log('ReviewCart: hybridConnectorsObj.N?.id:', hybridConnectorsObj.N?.id);
    }
    
    // ALWAYS log this to ensure we see it
    console.log('ReviewCart: ALWAYS LOG - hybridConnectorsObj.J?.id:', hybridConnectorsObj.J?.id);
    console.log('ReviewCart: ALWAYS LOG - hybridConnectorsObj.N?.id:', hybridConnectorsObj.N?.id);
    
    // Check each connector individually for debugging
    const hasI = !!(hybridConnectorsObj.I?.id && hybridConnectorsObj.I.id.trim() !== '');
    const hasJ = !!(hybridConnectorsObj.J?.id && hybridConnectorsObj.J.id.trim() !== '');
    const hasK = !!(hybridConnectorsObj.K?.id && hybridConnectorsObj.K.id.trim() !== '');
    const hasL = !!(hybridConnectorsObj.L?.id && hybridConnectorsObj.L.id.trim() !== '');
    const hasM = !!(hybridConnectorsObj.M?.id && hybridConnectorsObj.M.id.trim() !== '');
    const hasN = !!(hybridConnectorsObj.N?.id && hybridConnectorsObj.N.id.trim() !== '');
    
    missingConnector = !hasI || !hasJ || !hasK || !hasL || !hasM || !hasN;
    }
  } else {
    // 1:1 or standard - use connector property instead of id
    missingConnector = !endA.connector || !endB.connector;
  }
  
  // Show warning if either cable type or connectors are missing
  // Only show warning if config is fully loaded and connectors are actually missing
  const showWarning = useMemo(() => {
    // For hybrid cables, ensure we have valid hybrid connectors object before showing warning
    if (config.style === 'hybrid-power-audio-audio') {
      const hybridConnectorsObj = config.hybridConnectors?.connectors || config.hybridConnectors || {};
      const hasValidConnectors = Object.keys(hybridConnectorsObj).length >= 6; // Should have I,J,K,L,M,N
      
      if (!hasValidConnectors) {
        return false; // Don't show warning while connectors are still loading
      }
      
      // OVERRIDE: If all 6 connectors clearly have IDs, ignore missingConnector calculation
      const allConnectorsHaveIds = ['I', 'J', 'K', 'L', 'M', 'N'].every(key => {
        const connector = hybridConnectorsObj[key];
        return connector && connector.id && typeof connector.id === 'string' && connector.id.trim().length > 0;
      });
      
      if (allConnectorsHaveIds) {
        console.log('ReviewCart: OVERRIDE - All connectors clearly have IDs, ignoring missingConnector calculation');
        return missingCableType; // Only show warning for missing cable type, not connectors
      }
    }
    
    const shouldShowWarning = missingCableType || missingConnector;
    
    // ALWAYS log the showWarning calculation
    console.log('ReviewCart: ALWAYS LOG - showWarning calculation:', {
      missingCableType: missingCableType,
      missingConnector: missingConnector,
      showWarning: shouldShowWarning,
      configStyle: config.style,
      timestamp: new Date().toISOString()
    });
    
    return shouldShowWarning;
  }, [missingCableType, missingConnector, config.style, config.hybridConnectors]);
  
  const [priceInfo, setPriceInfo] = useState({ totalCost: 0 });
  const [priceLoading, setPriceLoading] = useState(false);
  
  // Debug: Track config changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && config?.style === 'hybrid-power-audio-audio') {
      console.log('ReviewCart: Config changed - hybrid connectors:', {
        hybridConnectors: config.hybridConnectors,
        connectors: config.hybridConnectors?.connectors,
        timestamp: new Date().toISOString()
      });
      
      // Additional detailed logging for config changes
      const hybridConnectorsObj = config.hybridConnectors?.connectors || config.hybridConnectors || {};
      console.log('ReviewCart: Config change - detailed connector state:', {
        I: { exists: !!hybridConnectorsObj.I, id: hybridConnectorsObj.I?.id, hasId: !!hybridConnectorsObj.I?.id },
        J: { exists: !!hybridConnectorsObj.J, id: hybridConnectorsObj.J?.id, hasId: !!hybridConnectorsObj.J?.id },
        K: { exists: !!hybridConnectorsObj.K, id: hybridConnectorsObj.K?.id, hasId: !!hybridConnectorsObj.K?.id },
        L: { exists: !!hybridConnectorsObj.L, id: hybridConnectorsObj.L?.id, hasId: !!hybridConnectorsObj.L?.id },
        M: { exists: !!hybridConnectorsObj.M, id: hybridConnectorsObj.M?.id, hasId: !!hybridConnectorsObj.M?.id },
        N: { exists: !!hybridConnectorsObj.N, id: hybridConnectorsObj.N?.id, hasId: !!hybridConnectorsObj.N?.id },
        allKeys: Object.keys(hybridConnectorsObj)
      });
    }
  }, [config]);

  // Manage cached SKU for consistent cart behavior
  useEffect(() => {
    if (config) {
      // Simplified approach: Always generate SKU with timestamp for consistency
      // The SKU service now handles all the complexity internally
      const sku = generateSKU(config);
      setCachedSku(sku);
    }
  }, [config]);

  // Clear product URL when config significantly changes (indicating a new build/reset)
  useEffect(() => {
    // Only clear if we have a product URL and a significant config change
    if (productUrl && config) {
      try {
        const currentSku = cachedSku || generateSKU(config);
        // Match the new URL format: /custom-cable-[SKU]?showHidden=true
        const savedSku = productUrl.match(/\/custom-cable-([^?]+)/)?.[1];
        
        if (savedSku && currentSku) {
          // Normalize: remove special chars but preserve decimal points, uppercase
          let normSaved = savedSku.replace(/[^A-Z0-9.-]/gi, '').toUpperCase();
          const normCurrent = currentSku.replace(/[^A-Z0-9.-]/gi, '').toUpperCase();

          // If saved includes a trailing timestamp suffix like -YYYYMMDD-HHMMSS, strip it for comparison
          normSaved = normSaved.replace(/-\d{8}-\d{6}$/,'');
          const normCurrentWithoutTimestamp = normCurrent.replace(/-\d{8}-\d{6}$/,'');

          // Consider equal if they match ignoring timestamp suffix
          const matchesIgnoringSuffix = normSaved === normCurrentWithoutTimestamp;
          const shouldClear = !matchesIgnoringSuffix && normCurrentWithoutTimestamp.length > 10;
          
          if (shouldClear) {
            const originalSavedSkuUpper = (savedSku || '').toString().toUpperCase();
            const originalCurrentSkuUpper = (currentSku || '').toString().toUpperCase();
            console.log('ReviewCart: Significant SKU change detected, clearing product URL:', {
              savedSku: normSaved,
              currentSku: normCurrentWithoutTimestamp,
              originalSavedSku: originalSavedSkuUpper,
              originalCurrentSku: originalCurrentSkuUpper,
              normalizedSavedSku: normSaved,
              normalizedCurrentSku: normCurrentWithoutTimestamp,
              reason: 'Does not match ignoring timestamp suffix'
            });
            setProductUrl('');
          } else {
            const originalSavedSkuUpper = (savedSku || '').toString().toUpperCase();
            const originalCurrentSkuUpper = (currentSku || '').toString().toUpperCase();
            console.log('ReviewCart: SKU comparison - keeping product URL:', {
              savedSku: normSaved,
              currentSku: normCurrentWithoutTimestamp,
              originalSavedSku: originalSavedSkuUpper,
              originalCurrentSku: originalCurrentSkuUpper,
              normalizedSavedSku: normSaved,
              normalizedCurrentSku: normCurrentWithoutTimestamp,
              shouldClear: shouldClear
            });
          }
        }
      } catch (error) {
        console.log('ReviewCart: Error in SKU comparison:', error);
        // Don't clear on errors - keep the product URL
      }
    }
  }, [config, productUrl, cachedSku]);
  
  // Memoize safeConfig to prevent unnecessary recalculations
  const memoizedSafeConfig = useMemo(() => {
    if (!config?.type) return null;
    
    // For hybrid connectors, use the overall length from hybridConnectors.lengths.A (in inches)
    // For standard cables, convert feet to inches for pricing service
    let pricingLength = config.length;
    
    if (config.hybridConnectors?.lengths?.A) {
      // Hybrid connectors store lengths in inches, use directly
      pricingLength = config.hybridConnectors.lengths.A;
    } else if (config.length && !['hybrid', 'zip', 'zip-audio-audio', 'wye', 'hybrid-power-audio-audio'].includes(config.style)) {
      // Standard cables store length in feet, convert to inches for pricing
      pricingLength = Math.round(config.length * 12);
    }
    
    // Create the safe config structure directly here to avoid object recreation
    return {
      category: config.category || '',
      type: config.type,
      style: config.style || 'end-to-end',
      length: pricingLength, // Use the converted length in inches
      quality: config.quality || '',
      endA: endA || {},
      endB: endB || {},
      endC: endC || {},
      finishes: config.finishing || config.finishes || { heatShrink: 'none', techFlex: 'none', labels: 'none' },
      hybridConnectors: hybridConnectors || {},
      wireGauge: config.wireGauge || {}
    };
  }, [config, endA, endB, endC, hybridConnectors]);

  useEffect(() => {
    let isMounted = true;
    
    async function fetchPrice() {
      if (calculateCablePrice && memoizedSafeConfig) {
        setPriceLoading(true);
        try {
          const result = await calculateCablePrice(memoizedSafeConfig);
          if (isMounted) {
            setPriceInfo(result);
          }
        } catch (error) {
          console.error('Price calculation error:', error);
          if (isMounted) {
            setPriceInfo({ totalCost: 0 });
          }
        } finally {
          if (isMounted) {
            setPriceLoading(false);
          }
        }
      }
    }
    fetchPrice();
    return () => { isMounted = false; };
  }, [memoizedSafeConfig]);

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price || 0), 0);
  };

  const getTypeName = (typeId) => {
    const found = (cableTypes || []).find(t => t.id === typeId);
    return found ? found.name : typeId || '(not selected)';
  };

  const renderConfigDetails = () => {
    const finishing = config.finishing || {};
    const hybridConnectors = config.hybridConnectors || {};
    const lengths = config.hybridConnectors?.lengths || config.lengths || {};
    // Generate SKU with error handling
    let sku = 'Pending Configuration';
    try {
      console.log('🔍 ReviewCart SKU Generation Debug:', {
        configWireType: config.wireType,
        configAudioWireType: config.audioWireType,
        configType: config.type,
        configStyle: config.style,
        connectors: config.connectors,
        fullConfig: config
      });
      sku = generateSKU(config);
      console.log('🔍 ReviewCart Generated SKU:', sku);
    } catch (error) {
      console.warn('Failed to generate SKU:', error);
    }

    // Helper to format lengths
    const formatLengths = (lengthsObj) => {
      return Object.entries(lengthsObj)
        .filter(([k]) => ['A','B','C','D','E','F','G','Overall'].includes(k))
        .map(([k, v]) => {
          // For hybrid connectors, lengths are stored in inches, convert to feet and inches
          if (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio') {
            const totalInches = parseFloat(v);
            const feet = Math.floor(totalInches / 12);
            const inches = Math.round(totalInches % 12);
            if (feet > 0 && inches > 0) {
              return `${k}: ${feet}' ${inches}"${k === 'Overall' ? ' (total)' : ''}`;
            } else if (feet > 0) {
              return `${k}: ${feet}'${k === 'Overall' ? ' (total)' : ''}`;
            } else {
              return `${k}: ${inches}"${k === 'Overall' ? ' (total)' : ''}`;
            }
          } else {
            // For standard cables, length is already in feet
            return `${k}: ${v} ft${k === 'Overall' ? ' (total)' : ''}`;
          }
        })
        .join(', ');
    };

    // Helper to get cable visual info
    const getCableVisualInfo = () => {
      const cableType = config.type;
      const wireGauge = config.wireGauge?.gauge;
      const color = getCableColor(cableType, wireGauge);
      const thickness = getCableThickness(cableType, wireGauge);
      
      return {
        color,
        thickness,
        type: cableType,
        gauge: wireGauge,
        jacketColor: config.wireGauge?.color || 'Black'
      };
    };

    const cableInfo = getCableVisualInfo();

    let details = [
      { label: 'SKU', value: sku },
      { label: 'Type', value: getTypeName(config.type) },
    ];

    // Add cable visual representation
    details.push({
      label: 'Cable Specification',
      value: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: cableInfo.thickness,
              height: cableInfo.thickness,
              borderRadius: '50%',
              backgroundColor: cableInfo.color,
              border: '2px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }}
          >
            {cableInfo.type?.charAt(0).toUpperCase()}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {cableInfo.type?.toUpperCase()} Cable
            </Typography>
            {cableInfo.gauge && (
              <Typography variant="caption" color="text.secondary">
                {cableInfo.gauge} AWG, {cableInfo.jacketColor} jacket
              </Typography>
            )}
          </Box>
        </Box>
      )
    });

    if (config.style === 'hybrid' || config.style === 'zip' || config.style === 'zip-audio-audio' || config.style === 'wye' || config.style === 'hybrid-power-audio-audio') {
      // Show all hybrid/zip connectors and lengths
      details.push({
        label: 'Lengths',
        value: formatLengths(lengths)
      });
      // Show each connector based on the cable style
      let hybridLabels, labelToLengthKey;
      if (config.style === 'zip-audio-audio') {
        hybridLabels = ['G', 'H', 'I', 'J'];
        labelToLengthKey = { G: 'C', H: 'D', I: 'E', J: 'F' };
      } else if (config.style === 'hybrid-power-audio-audio') {
        hybridLabels = ['I', 'J', 'K', 'L', 'M', 'N'];
        labelToLengthKey = { I: 'C', J: 'D', K: 'E', L: 'F', M: 'G', N: 'H' };
      } else if (config.style === 'wye') {
        hybridLabels = ['E', 'F', 'G'];
        labelToLengthKey = { E: 'B', F: 'C', G: 'D' };
      } else {
        hybridLabels = ['G', 'H', 'I', 'J'];
        labelToLengthKey = { G: 'C', H: 'D', I: 'E', J: 'F' };
      }
      const hybridConnectorsObj = config.hybridConnectors?.connectors || config.hybridConnectors || {};
      hybridLabels.forEach(label => {
        const conn = hybridConnectorsObj[label];
        let display = 'Not selected';
        let isSelected = false;
        
        // Debug logging for connector objects
        if (process.env.NODE_ENV === 'development' && (label === 'J' || label === 'N')) {
          console.log(`ReviewCart: Connector ${label}:`, conn);
          console.log(`ReviewCart: Connector ${label} has id:`, conn?.id);
          console.log(`ReviewCart: Connector ${label} isSelected will be:`, !!(conn && conn.id));
        }
        
        // Check if connector is properly selected (has an id)
        if (conn && conn.id && conn.id.trim() !== '') {
          isSelected = true;
          display = getConnectorDisplayName(conn);
          if (conn.gender && !display.toLowerCase().includes(conn.gender.toLowerCase())) {
            display += ` (${conn.gender.charAt(0).toUpperCase() + conn.gender.slice(1)})`;
          }
          // Add configuration abbreviation
          if (conn.configuration) {
            const config = conn.configuration.toLowerCase();
            if (config === 'standard' || config === 'std') display += ' - STD';
            else if (config === 'right-angle' || config === 'right angle' || config === 'r/a') display += ' - RA';
            else if (config === 'panel-mount' || config === 'panel mount' || config === 'panel') display += ' - P';
          }
        }
        
        // Add length if available
        const lengthKey = labelToLengthKey[label];
        const lengthVal = lengths && lengthKey && lengths[lengthKey] !== undefined ? ` -- ${lengths[lengthKey]} in` : '';
        
        // Debug logging for final state
        if (process.env.NODE_ENV === 'development' && (label === 'J' || label === 'N')) {
          console.log(`ReviewCart: Connector ${label} final isSelected:`, isSelected);
          console.log(`ReviewCart: Connector ${label} will render icon:`, isSelected);
        }
        
        // Debug logging for icon rendering
        if (process.env.NODE_ENV === 'development' && (label === 'K' || label === 'L' || label === 'N')) {
          const icon = isSelected ? getConnectorIcon(conn) : 'not rendered';
          const color = isSelected ? getConnectorColor(conn) : 'not rendered';
          console.log(`ReviewCart: Rendering connector ${label}:`, {
            conn: conn,
            isSelected: isSelected,
            icon: icon,
            color: color,
            display: display,
            willRenderIcon: isSelected
          });
        }
        
        details.push({ 
          label: `Connector ${label}`, 
          value: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isSelected && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: getConnectorColor(conn),
                    border: '1px solid #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  {getConnectorIcon(conn)}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <span>
                  {display}{lengthVal}
                  {isXLR5Pin(conn) && (
                    <Tooltip title="Only 3 pins wired" arrow>
                      <span style={{ color: '#ff9800', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                    </Tooltip>
                  )}
                </span>
              </Box>
            </Box>
          )
        });
      });
    } else {
      // Standard cable
      details.push({
        label: 'Length',
        value: (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 0.5 }}>
              {config.length ? (Math.round(config.length * 12) / 12).toFixed(2) : '0.00'} ft
            </Typography>
            <Tooltip 
              title="Cable length measured to center of connector, exact length may vary"
              arrow
              placement="top"
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#ff6600', 
                  cursor: 'help',
                  fontWeight: 'bold'
                }}
              >
                *
              </Typography>
            </Tooltip>
          </Box>
        )
      });
      details.push({
        label: 'Connectors',
        value: (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {endA && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: getConnectorColor(endA),
                    border: '1px solid #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  {getConnectorIcon(endA)}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <span>
                  End A: {getConnectorDisplayName(endA)}
                  {isXLR5Pin(endA) && (
                    <Tooltip title="Only 3 pins wired" arrow>
                      <span style={{ color: '#ff9800', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                    </Tooltip>
                  )}
                  {endA.configuration && endA.configuration !== 'standard' && (
                    <span style={{ color: '#666', fontSize: '0.9em' }}>
                      {endA.configuration === 'panel-mount' ? ' (Panel Mount)' : 
                       endA.configuration === 'right-angle' ? ' (Right Angle)' : 
                       ` (${endA.configuration})`}
                    </span>
                  )}
                </span>
                
              </Box>
            </Box>
            {(endB.label || endB.description || endB.connector || endB.skuCode) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {endB && (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '4px',
                      backgroundColor: getConnectorColor(endB),
                      border: '1px solid #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.6rem',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  >
                    {getConnectorIcon(endB)}
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <span>
                    End B: {getConnectorDisplayName(endB)}
                    {isXLR5Pin(endB) && (
                      <Tooltip title="Only 3 pins wired" arrow>
                        <span style={{ color: '#ff9800', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                      </Tooltip>
                    )}
                    {endB.configuration && endB.configuration !== 'standard' && (
                      <span style={{ color: '#666', fontSize: '0.9em' }}>
                        {endB.configuration === 'panel-mount' ? ' (Panel Mount)' : 
                         endB.configuration === 'right-angle' ? ' (Right Angle)' : 
                         ` (${endB.configuration})`}
                      </span>
                    )}
                  </span>
 
                </Box>
              </Box>
            )}
          </Box>
        )
      });
    }

    // Add wire gauge information for power cables
    if (config.type === 'power' && config.wireGauge) {
      details.push({
        label: 'Wire Gauge',
        value: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: getCableThickness('power', config.wireGauge.gauge),
                height: getCableThickness('power', config.wireGauge.gauge),
                borderRadius: '50%',
                backgroundColor: getCableColor('power', config.wireGauge.gauge),
                border: '2px solid #e0e0e0'
              }}
            />
            <Typography variant="body2">
              {config.wireGauge.gauge} AWG, {config.wireGauge.color} jacket
            </Typography>
          </Box>
        )
      });
    }

    // Add wire type information for non-power cables
    if (config.type !== 'power') {
      // Use the same wire type determination logic as BOM generator service
      const wireType = getCorrectWireTypeForDisplay(config);
      if (wireType) {
        details.push({
          label: 'Wire Type',
          value: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: '#4a5568',
                  border: '2px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: 'bold'
                }}
              >
                W
              </Box>
              <Typography variant="body2">
                {wireType} - {getWireTypeDescription(wireType)}
              </Typography>
            </Box>
          )
        });
      }
    }

    const finishingRows = buildFinishingDisplayRows(finishing);

    details.push({
      label: 'Finishing',
      value: <FinishingDetailRows rows={finishingRows} />
    });

    // Add special instructions if they exist
    if (config.specialInstructions || hybridConnectors.specialInstructions) {
      const instructions = config.specialInstructions || hybridConnectors.specialInstructions;
      details.push({
        label: 'Special Instructions',
        value: (
          <Box sx={{ 
            p: 2, 
            bgcolor: '#f8f9fa', 
            borderRadius: 1, 
            border: '1px solid #e9ecef',
            maxWidth: '100%'
          }}>
            <Typography variant="body2" sx={{ 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#495057'
            }}>
              {instructions}
            </Typography>
          </Box>
        )
      });
    }

    return (
      <List>
        {details.map((detail, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemText
                primary={detail.label}
                secondary={detail.value}
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
            {index < details.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        {/* Order fulfillment notice */}
        <ListItem>
          <ListItemText
            primary={
              <Box sx={{ 
                p: 2, 
                bgcolor: '#fff3cd', 
                borderRadius: 1, 
                border: '1px solid #ffeaa7',
                maxWidth: '100%'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#856404',
                  fontWeight: 500,
                  lineHeight: 1.5
                }}>
                  Most cables are built within a week but some hybrid builds may take up to 4 weeks to fulfill. Please contact us for lead time or with need-by dates and we will do our best to accommodate wherever possible
                </Typography>
              </Box>
            }
            primaryTypographyProps={{ component: 'div' }}
          />
        </ListItem>
      </List>
    );
  };

  // Helper function to get connector color
  const getConnectorColor = (connector) => {
    if (!connector) return '#cbd5e0';
    
    // Debug logging for connector color detection
    if (process.env.NODE_ENV === 'development') {
      console.log('ReviewCart: getConnectorColor called with:', {
        connector: connector,
        id: connector.id,
        type: connector.type,
        label: connector.label,
        skuCode: connector.skuCode
      });
    }
    
    // XLR connectors
    if (connector.id?.includes('NC3') || connector.label?.toLowerCase().includes('xlr')) {
      return '#2d3748';
    }
    
    // Speakon connectors
    if (connector.id?.includes('NL') || connector.label?.toLowerCase().includes('speakon')) {
      return '#3182ce';
    }
    
    // Power connectors (IEC, PowerCON, NEMA, etc.)
    if (connector.id?.includes('IEC') || connector.id?.includes('PowerCON') || connector.id?.includes('NEMA') || 
        connector.type?.toLowerCase().includes('powercon') || connector.type?.toLowerCase().includes('iec') || connector.type?.toLowerCase().includes('nema') ||
        connector.label?.toLowerCase().includes('iec') || connector.label?.toLowerCase().includes('powercon') || 
        connector.label?.toLowerCase().includes('nema') ||
        connector.skuCode?.toLowerCase().includes('iec') || connector.skuCode?.toLowerCase().includes('powercon') || connector.skuCode?.toLowerCase().includes('nema')) {
      return '#dd6b20';
    }
    
    // RCA connectors
    if (connector.id?.includes('RCA') || connector.label?.toLowerCase().includes('rca')) {
      return '#e53e3e';
    }
    
    // Default
    return '#4a5568';
  };

  // Helper function to get connector icon
  const getConnectorIcon = (connector) => {
    if (!connector) return '?';
    
    // Debug logging for connector icon detection
    if (process.env.NODE_ENV === 'development') {
      console.log('ReviewCart: getConnectorIcon called with:', {
        connector: connector,
        id: connector.id,
        type: connector.type,
        label: connector.label,
        skuCode: connector.skuCode
      });
    }
    
    // XLR connectors
    if (connector.id?.includes('NC3') || connector.label?.toLowerCase().includes('xlr')) {
      return 'X';
    }
    
    // Speakon connectors
    if (connector.id?.includes('NL') || connector.label?.toLowerCase().includes('speakon')) {
      return 'S';
    }
    
    // Power connectors (IEC, PowerCON, NEMA, etc.)
    if (connector.id?.includes('IEC') || connector.id?.includes('PowerCON') || connector.id?.includes('NEMA') || 
        connector.type?.toLowerCase().includes('powercon') || connector.type?.toLowerCase().includes('iec') || connector.type?.toLowerCase().includes('nema') ||
        connector.label?.toLowerCase().includes('iec') || connector.label?.toLowerCase().includes('powercon') || 
        connector.label?.toLowerCase().includes('nema') ||
        connector.skuCode?.toLowerCase().includes('iec') || connector.skuCode?.toLowerCase().includes('powercon') || connector.skuCode?.toLowerCase().includes('nema')) {
      return 'P';
    }
    
    // RCA connectors
    if (connector.id?.includes('RCA') || connector.label?.toLowerCase().includes('rca')) {
      return 'R';
    }
    
    // Default
    return 'C';
  };

  // const cableName = config.name || 'NLFX Custom Cable'; // Removed unused variable

  // Defensive: ensure cart is always an array before using .length or .map
  const safeCart = Array.isArray(cart) ? cart : [];



  const handleExportPNG = async () => {
    if (!canGenerateSVG(config)) {
      setExportMessage('PNG export is not available for this cable configuration');
      setExportSuccess(false);
      return;
    }
    
    // Debug logging to help verify the fix
    console.log('🔍 [PNG-DOWNLOAD] ReviewCart config check:', {
      hasImageUrl: !!config.imageUrl,
      imageUrl: config.imageUrl,
      hasHybridConnectors: !!config.hybridConnectors,
      hybridConnectorsImageUrl: config.hybridConnectors?.imageUrl,
      configKeys: Object.keys(config)
    });
    
    // Check if we have an existing PNG image URL
    if (config.imageUrl) {
      console.log('📥 [PNG-DOWNLOAD] Using existing PNG from S3:', config.imageUrl);
      try {
        setExporting(true);
        
        // Fetch the image as a blob to ensure proper download
        const response = await fetch(config.imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        // Create a blob URL for download
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Extract filename from URL or generate one
        const urlParts = config.imageUrl.split('/');
        const filename = urlParts[urlParts.length - 1] || 'cable-diagram.png';
        
        // Create download link
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up blob URL
        window.URL.revokeObjectURL(blobUrl);
        
        setExportMessage(`PNG downloaded successfully: ${filename}`);
        setExportSuccess(true);
      } catch (error) {
        console.error('PNG download error:', error);
        setExportMessage('Failed to download PNG. Please try again.');
        setExportSuccess(false);
      } finally {
        setExporting(false);
      }
    } else {
      setExportMessage('No PNG image available. Please generate the cable image first.');
      setExportSuccess(false);
    }
  };

  // Handle BigCommerce integration
  const handleAddToBigCommerce = async () => {
    console.log('=== ADD TO CART BUTTON CLICKED ===');
    const cartWindow = openCartTabDuringGesture();
    setBigCommerceLoading(true);
    setBigCommerceMessage('');
    setBigCommerceSuccess(false);
    setCartUrl('');
    setProductUrl('');
    setPopupBlocked(false);
    
    try {
      // Ensure price is calculated
      let finalPrice = priceInfo?.totalCost || 0;
      
      if (!finalPrice && memoizedSafeConfig && calculateCablePrice) {
        console.log('Calculating price for BigCommerce...');
        setPriceLoading(true);
        try {
          const recalculatedPrice = await calculateCablePrice(memoizedSafeConfig);
          finalPrice = recalculatedPrice?.totalCost || 0;
        } catch (error) {
          console.error('Price calculation error in BigCommerce:', error);
          finalPrice = 0;
        } finally {
          setPriceLoading(false);
        }
      }

      if (!finalPrice) {
        throw new Error('Unable to calculate price for this configuration');
      }

      // Use cached SKU for consistent cart behavior
      const sku = cachedSku || generateSKU(config);

      // Capture and upload the cable visualization before hitting BigCommerce.
      // 1:1 cables: do not reuse connector-step image (captured before length is set).
      let imageUrl =
        isStandardEndToEndStyle(config.style) ? null : config.imageUrl || null;
      
      // Debug logging for BigCommerce integration
      console.log('🔍 [BIGCOMMERCE] Image URL check:', {
        hasImageUrl: !!config.imageUrl,
        imageUrl: config.imageUrl,
        hasHybridConnectors: !!config.hybridConnectors,
        hybridConnectorsImageUrl: config.hybridConnectors?.imageUrl
      });
      if (svgRef && svgRef.current) {
        const captureResult = await cableImageCaptureService.captureCableImage(
          svgRef.current,
          config.style,
          { ...config, sku }
        );
        if (captureResult.success) {
          if (captureResult.s3Url) {
            imageUrl = captureResult.s3Url;
          } else if (captureResult.imageData) {
            try {
              const resp = await fetch(API_ENDPOINTS.UPLOAD_DIAGRAM, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sku,
                  imageData: captureResult.imageData,
                  contentType: 'image/png'
                })
              });
              if (resp.ok) {
                const data = await resp.json();
                imageUrl = data.url;
              } else {
                const err = await resp.text();
                console.warn('Failed to upload to S3:', err);
              }
            } catch (e) {
              console.warn('Error uploading captured image to S3:', e);
            }
          }
        } else {
          console.warn('⚠️ Failed to capture cable image:', captureResult.error);
        }
      } else {
        if (imageUrl) {
        } else {
          
          // For BaseConnectorSelector (standard / 1:1 cables), generate via main image service
          if (
            config.style === undefined ||
            config.style === 'end-to-end' ||
            config.style === 'standard' ||
            config.style === '1:1'
          ) {
            try {
              // Enhance config with complete connector and length data for better SVG generation
              const enhancedConfig = {
                ...config,
                sku,
                connectors: {
                  endA: config.connectors?.endA || config.endA,
                  endB: config.connectors?.endB || config.endB
                },
                length: resolveLengthFeetForImage({ ...config, sku }),
                wireType: config.wireType || config.audioWireType || 'PSM',
                unit: 'ft'
              };
              
              
              // Try server-side diagram (same /generate-cable-image as complex cables; avoids legacy :3003)
              let ec2Success = false;
              
              try {
                const serverStyle =
                  config.style === 'standard' ? 'standard' : 'end-to-end';
                const genResult = await cableImageCaptureService.generateCableImage(
                  enhancedConfig,
                  serverStyle,
                  { forceRegenerate: true }
                );
                if (genResult.success && genResult.s3Url) {
                  imageUrl = genResult.s3Url;
                  ec2Success = true;
                } else if (genResult.error) {
                  console.warn('Server cable image generation failed:', genResult.error);
                }
              } catch (e) {
                console.warn('Server cable image generation failed, falling back to local generation:', e.message);
              }
              
              // If EC2 failed, use local generation
              if (!ec2Success) {
                try {
                  // Try DOM capture first (more reliable), then fallback to SVG generation
                  let localResult;
                  
                  try {
                    const pngData = await captureCableConfiguration();
                    
                    if (pngData) {
                      localResult = {
                        success: true,
                        imageData: pngData,
                        svgData: null
                      };
                    } else {
                      throw new Error('DOM capture returned no data');
                    }
                  } catch (captureError) {
                    console.warn('⚠️ [LOCAL] DOM capture failed, falling back to SVG generation:', captureError);
                    
                    // Fallback to SVG generation
                    localResult = await generateEndToEndImage(enhancedConfig);
                  }
                  
                  if (localResult.success) {
                    
                    // Upload the locally generated image to S3
                    const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_DIAGRAM, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        sku,
                        imageData: localResult.imageData,
                        contentType: 'image/png'
                      })
                    });
                    
                    if (uploadResponse.ok) {
                      const uploadData = await uploadResponse.json();
                      imageUrl = uploadData.url;
                    } else {
                      const uploadError = await uploadResponse.text();
                      console.warn('⚠️ Failed to upload local image to S3:', uploadError);
                    }
                  } else {
                    console.warn('⚠️ Local image generation failed:', localResult.error);
                  }
                } catch (localError) {
                  console.warn('⚠️ Error in local image generation:', localError);
                }
              }
            } catch (error) {
              console.warn('⚠️ Error in fallback image generation:', error);
            }
          } else {
            console.log('No fallback generation available for cable style:', config.style);
          }
        }
      }

      // Create configuration with SKU (and optional imageUrl) for BigCommerce
      const configWithSku = {
        ...config,
        sku,
        ...(imageUrl ? { imageUrl } : {})
      };
      
      // Call the BigCommerce API endpoint
      const response = await fetch(API_ENDPOINTS.BIGCOMMERCE_CART, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          config: configWithSku,
          price: finalPrice
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('=== API RESPONSE ===', result);
      
      if (result.success) {
        console.log('=== SUCCESS: Product created successfully ===');
        console.log('Product ID:', result.result.product_id);
        console.log('Cart URL:', result.result.cart_url);
        console.log('Redirect URL:', result.result.redirect_url);
        
        // Update product photo if we have an image URL
        if (imageUrl && result.result.product_id) {
          try {
            const photoUpdateResponse = await fetch(API_ENDPOINTS.BIGCOMMERCE_CART, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                product_id: result.result.product_id,
                image_url: imageUrl,
                sku: result.result.sku
              })
            });
            
            if (photoUpdateResponse.ok) {
              const photoResult = await photoUpdateResponse.json();
            } else {
              const photoError = await photoUpdateResponse.text();
              console.warn('⚠️ [PHOTO-UPDATE] Failed to update product photo:', photoError);
            }
          } catch (photoError) {
            console.warn('⚠️ [PHOTO-UPDATE] Error updating product photo:', photoError);
          }
        }
        
        setBigCommerceMessage(`Product created! SKU: ${result.result.sku}`);
        setBigCommerceSuccess(true);
        setCartUrl(result.result.cart_url || 'https://www.nlfxpro.com/cart.php');
        
        // Generate product URL with the actual BigCommerce SKU (includes timestamp)
        const actualSku = result.result.sku; // This is the SKU with timestamp that BigCommerce actually created
        const productPageUrl = `https://www.nlfxpro.com/custom-cable-${actualSku.toLowerCase()}?showHidden=true`;
        setProductUrl(productPageUrl);
        
        console.log('=== PRODUCT URL GENERATED ===', {
          bigCommerceSku: actualSku,
          productPageUrl: productPageUrl,
          cachedSkuUsed: sku
        });
        
        // Navigate pre-opened cart tab (opened on click to avoid popup blockers)
        const redirectUrl = result.result.redirect_url || result.result.cart_url;
        console.log('=== AUTO-OPENING CART ===', redirectUrl);

        if (redirectUrl) {
          const navigated = navigateCartTab(cartWindow, redirectUrl);
          if (navigated) {
            setPopupBlocked(false);
            setBigCommerceMessage(
              `Product created and added to cart! SKU: ${result.result.sku}`
            );
          } else {
            console.log('Popup blocked - showing fallback message');
            setPopupBlocked(true);
            setBigCommerceMessage(
              `Product created! Popup blocked - use "Add to Cart" button to add item.`
            );
          }
        } else {
          closeCartTab(cartWindow);
        }
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
      
    } catch (error) {
      console.error('BigCommerce integration error:', error);
              setBigCommerceMessage(`Failed to add to cart: ${error.message}`);
      setBigCommerceSuccess(false);
      closeCartTab(cartWindow);
    } finally {
      setBigCommerceLoading(false);
    }
  };




  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cable Configuration
              </Typography>
              {renderConfigDetails()}
              {(() => {
                // Debug log right before rendering warning
                console.log('ReviewCart: RENDERING WARNING CHECK:', {
                  showWarning,
                  missingCableType,
                  missingConnector,
                  configStyle: config.style,
                  hybridConnectorsObj: config.hybridConnectors?.connectors || config.hybridConnectors || {},
                  timestamp: new Date().toISOString()
                });
                return showWarning;
              })() && (
                <Box sx={{ mt: 2 }}>
                  <Typography color="error" variant="body2">
                    Warning: {missingCableType ? 'Please select a cable type.' : ''}{missingCableType && missingConnector ? ' ' : ''}{missingConnector ? 'Please select connectors for all required ends.' : ''}
                  </Typography>
                </Box>
              )}
              
              {hasAnyCustomHeatShrinkEnd(config.finishing) && !isCustomHeatShrinkArtworkComplete(config.finishing) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Complete your 2&quot; custom printed heat shrink artwork: upload a logo and confirm it is black and
                  white, or enter text (up to two lines, 12 characters each). Quantity discounts are available —
                  contact us for info.
                </Alert>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={bigCommerceLoading ? <CircularProgress size={20} /> : <AddIcon />}
                  onClick={handleAddToBigCommerce}
                  sx={{ flex: 1 }}
                  disabled={
                    bigCommerceLoading ||
                    priceLoading ||
                    !priceInfo ||
                    typeof priceInfo.totalCost !== 'number' ||
                    (hasAnyCustomHeatShrinkEnd(config.finishing) && !isCustomHeatShrinkArtworkComplete(config.finishing))
                  }
                  title="Add to Cart"
                >
                  {bigCommerceLoading ? (
                    'Adding to Cart...'
                  ) : priceLoading ? (
                    <>Calculating Price...</>
                  ) : priceInfo && typeof priceInfo.totalCost === 'number' && priceInfo.totalCost > 0 ? (
                    <>Add to Cart – {formatPrice(priceInfo.totalCost)}</>
                  ) : (
                    'Price unavailable'
                  )}
                </Button>
                {canGenerateSVG(config) && (
                  <Button
                    variant="outlined"
                    startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                    onClick={handleExportPNG}
                    disabled={exporting}
                    sx={{ minWidth: 'auto' }}
                    title="Download PNG diagram (raster format)"
                  >
                    {exporting ? '' : 'PNG'}
                  </Button>
                )}
                {canGenerateBOM(config) && (
                  <Button
                    variant="outlined"
                    startIcon={<BuildIcon />}
                    onClick={() => setBomDialogOpen(true)}

                    sx={{ minWidth: 'auto' }}
                    title="Generate Bill of Materials"
                  >
                    BOM
                  </Button>
                )}
              </Box>
               
              {/* View Product button - persistent until reset/new product */}
              {productUrl && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      console.log('ReviewCart: Opening product URL:', productUrl);
                      window.open(productUrl, '_blank');
                    }}
                    title="View Custom Cable Product Page"
                    sx={{ 
                      minWidth: 200,
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                      }
                    }}
                  >
                    View Product Page
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'none' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cart Items
              </Typography>
              {safeCart.length === 0 ? (
                <Typography color="text.secondary">
                  Your cart is empty
                </Typography>
              ) : (
                <>
                  <List>
                    {safeCart.map((item, index) => (
                      <React.Fragment key={index}>
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={() => onRemoveFromCart && onRemoveFromCart(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              variant="rounded"
                              sx={{ 
                                width: 60, 
                                height: 45, 
                                cursor: item.diagramUrl ? 'pointer' : 'default',
                                border: item.diagramUrl ? '2px solid #1976d2' : '2px solid #e0e0e0',
                                backgroundColor: 'white',
                                '&:hover': item.diagramUrl ? {
                                  border: '2px solid #1565c0',
                                  transform: 'scale(1.05)',
                                  transition: 'all 0.2s ease-in-out',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                } : {}
                              }}
                              onClick={item.diagramUrl ? () => window.open(item.diagramUrl, '_blank') : undefined}
                            >
                              {item.diagramUrl ? (
                                <img
                                  src={item.diagramUrl}
                                  alt="Cable Diagram"
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'contain',
                                    borderRadius: '6px'
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: item.diagramUrl ? 'none' : 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: '6px',
                                  color: '#666',
                                  position: 'relative'
                                }}
                              >
                                {/* Cable representation */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: getCableThickness(item.type, item.wireGauge?.gauge),
                                    height: getCableThickness(item.type, item.wireGauge?.gauge),
                                    borderRadius: '50%',
                                    backgroundColor: getCableColor(item.type, item.wireGauge?.gauge),
                                    border: '1px solid #ccc'
                                  }}
                                />
                                {/* Connector indicators */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: '2px',
                                    left: '2px',
                                    width: 12,
                                    height: 12,
                                    borderRadius: '2px',
                                    backgroundColor: getConnectorColor(item.endA),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.5rem',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {getConnectorIcon(item.endA)}
                                </Box>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: '2px',
                                    right: '2px',
                                    width: 12,
                                    height: 12,
                                    borderRadius: '2px',
                                    backgroundColor: getConnectorColor(item.endB),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.5rem',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {getConnectorIcon(item.endB)}
                                </Box>
                              </Box>
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle1">
                                  {item.name || 'NLFX Custom Cable'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  SKU: {item.sku || 'Pending Configuration'}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                  <span style={{ marginRight: '4px' }}>
                                    {(() => {
                                      // For hybrid/zip cables, show overall length from hybridConnectors
                                      if (item.style === 'hybrid' || item.style === 'zip' || item.style === 'zip-audio-audio' || item.style === 'wye' || item.style === 'hybrid-power-audio-audio') {
                                        const overallLength = item.hybridConnectors?.lengths?.Overall;
                                        if (overallLength) {
                                          // Convert inches to feet and round to nearest inch in feet
                                          const lengthInFeet = Math.round(overallLength / 12 * 12) / 12;
                                          return `${lengthInFeet.toFixed(2)} ft`;
                                        }
                                      }
                                      // For standard cables, use item.length
                                      return item.length ? `${(Math.round(item.length * 12) / 12).toFixed(2)} ft` : '0.00 ft';
                                    })()}
                                  </span>
                                  <Tooltip 
                                    title="Cable length measured to center of connector, exact length may vary"
                                    arrow
                                    placement="top"
                                  >
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: '#ff6600', 
                                        cursor: 'help',
                                        fontWeight: 'bold',
                                        mr: 1
                                      }}
                                    >
                                      *
                                    </Typography>
                                  </Tooltip>
                                  <span>- {item.quality || 'Standard'}</span>
                                </Typography>
                                <Typography variant="body2">
                                  {(() => {
                                    // Helper function for configuration abbreviation (defined locally)
                                    const getConfigAbbrev = (configuration) => {
                                      if (!configuration) return '';
                                      const config = configuration.toLowerCase();
                                      if (config === 'standard' || config === 'std') return 'STD';
                                      if (config === 'right-angle' || config === 'right angle' || config === 'r/a') return 'RA';
                                      if (config === 'panel-mount' || config === 'panel mount' || config === 'panel') return 'P';
                                      return '';
                                    };

                                    const formatConnector = (end) => {
                                      if (!end?.connector && !end?.label && !end?.id) return '';
                                      
                                      // Use the same display name function as the configuration section
                                      let str = getConnectorDisplayName(end);
                                      
                                      // Add XLR 5-pin asterisk
                                      if (isXLR5Pin(end)) {
                                        str += '*';
                                      }
                                      
                                      // Add configuration abbreviation
                                      const configAbbrev = getConfigAbbrev(end.variant || end.configuration);
                                      if (configAbbrev) {
                                        str += ` (${configAbbrev})`;
                                      } else if (end.variant && end.variant !== 'standard') {
                                        let variantLabel = end.variant;
                                        if (variantLabel === 'panel-mount') variantLabel = 'Panel-Mount';
                                        else if (variantLabel === 'inline') variantLabel = 'Inline';
                                        else variantLabel = variantLabel.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase());
                                        str += ` (${variantLabel})`;
                                      }
                                      return str;
                                    };
                                    
                                    // Handle hybrid/zip cables
                                    if (item.style === 'hybrid' || item.style === 'zip' || item.style === 'zip-audio-audio' || item.style === 'wye' || item.style === 'hybrid-power-audio-audio') {
                                      const hybridConnectors = item.hybridConnectors?.connectors || {};
                                      const lengths = item.hybridConnectors?.lengths || {};
                                      let labelToLengthKey;
                                      if (item.style === 'zip-audio-audio') {
                                        labelToLengthKey = { G: 'C', H: 'D', I: 'E', J: 'F' };
                                      } else if (item.style === 'hybrid-power-audio-audio') {
                                        labelToLengthKey = { I: 'C', J: 'D', K: 'E', L: 'F', M: 'G', N: 'H' };
                                      } else if (item.style === 'wye') {
                                        labelToLengthKey = { E: 'B', F: 'C', G: 'D' };
                                      } else {
                                        labelToLengthKey = { G: 'C', H: 'D', I: 'E', J: 'F' };
                                      }
                                      const connectorLabels = Object.keys(hybridConnectors).filter(key => hybridConnectors[key]?.id || hybridConnectors[key]?.label);
                                      if (connectorLabels.length > 0) {
                                        return connectorLabels.map(key => {
                                          const connector = hybridConnectors[key];
                                          const displayName = getConnectorDisplayName(connector);
                                          const configAbbrev = getConfigAbbrev(connector.configuration);
                                          const xlrAsterisk = isXLR5Pin(connector) ? '*' : '';
                                          const lengthKey = labelToLengthKey[key];
                                          const lengthVal = lengths && lengthKey && lengths[lengthKey] !== undefined ? ` -- ${lengths[lengthKey]} in` : '';
                                          return `${key}: ${displayName}${xlrAsterisk}${configAbbrev ? ` (${configAbbrev})` : ''}${lengthVal}`;
                                        }).join(', ');
                                      }
                                      return 'Hybrid/Zip Cable';
                                    }
                                    
                                    // Handle standard cables
                                    if (item.endA?.connector && item.endB?.connector) {
                                      return `${formatConnector(item.endA)} to ${formatConnector(item.endB)}`;
                                    } else if (item.endA?.connector) {
                                      return formatConnector(item.endA);
                                    } else if (item.endB?.connector) {
                                      return formatConnector(item.endB);
                                    } else {
                                      return '';
                                    }
                                  })()}
                                </Typography>
                                
                                {/* Add connector details for one-to-one cables */}
                                {(!item.style || item.style === 'end-to-end' || item.style === '1:1') && item.endA?.connector && item.endB?.connector && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary" component="div">
                                      <strong>Connectors:</strong>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" component="div" sx={{ ml: 1 }}>
                                      End A: {getConnectorDisplayName(item.endA)}
                                      {item.endA.configuration && item.endA.configuration !== 'standard' && (
                                        <span style={{ color: '#666', fontSize: '0.9em' }}>
                                          {item.endA.configuration === 'panel-mount' ? ' (Panel Mount)' : 
                                           item.endA.configuration === 'right-angle' ? ' (Right Angle)' : 
                                           ` (${item.endA.configuration})`}
                                        </span>
                                      )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" component="div" sx={{ ml: 1 }}>
                                      End B: {getConnectorDisplayName(item.endB)}
                                      {item.endB.configuration && item.endB.configuration !== 'standard' && (
                                        <span style={{ color: '#666', fontSize: '0.9em' }}>
                                          {item.endB.configuration === 'panel-mount' ? ' (Panel Mount)' : 
                                           item.endB.configuration === 'right-angle' ? ' (Right Angle)' : 
                                           ` (${item.endB.configuration})`}
                                        </span>
                                      )}
                                    </Typography>
                                  </Box>
                                )}
                                {/* Wire Gauge details for power cables */}
                                {item.type === 'power' && item.wireGauge && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Wire: {item.wireGauge.gauge} AWG, {item.wireGauge.color} jacket
                                    </Typography>
                                  </Box>
                                )}
                                
                                {/* Wire Type details for non-power cables */}
                                {item.type !== 'power' && (() => {
                                  const itemWireType = getCorrectWireTypeForDisplay(item);
                                  return itemWireType ? (
                                    <Box sx={{ mt: 1 }}>
                                      <Typography variant="body2" color="text.secondary">
                                        Wire: {itemWireType} - {getWireTypeDescription(itemWireType)}
                                      </Typography>
                                    </Box>
                                  ) : null;
                                })()}
                                
                                {/* Finishing details for cart item */}
                                {item.finishing && (
                                  <Box sx={{ mt: 1 }}>
                                    <FinishingDetailRows
                                      rows={buildFinishingDisplayRows(item.finishing)}
                                      muted
                                    />
                                  </Box>
                                )}
                                
                                {/* Special Instructions for cart item */}
                                {(item.specialInstructions || item.hybridConnectors?.specialInstructions) && (
                                  <Box sx={{ 
                                    mt: 1, 
                                    p: 1, 
                                    backgroundColor: '#f8f9fa', 
                                    borderRadius: 1,
                                    border: '1px solid #e9ecef'
                                  }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                      Special Instructions:
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ 
                                      display: 'block', 
                                      mt: 0.5,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}>
                                      {item.specialInstructions || item.hybridConnectors?.specialInstructions}
                                    </Typography>
                                  </Box>
                                )}
                                
                                {/* Price display */}
                                {item.price && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                                      ${item.price.toFixed(2)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                        {index < safeCart.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6" align="right">
                      Total: {formatPrice(calculateTotal())}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={!!exportMessage}
        autoHideDuration={6000}
        onClose={() => setExportMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setExportMessage('')} 
          severity={exportSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {exportMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!bigCommerceMessage}
        onClose={() => {
          setBigCommerceMessage('');
          setCartUrl('');
          setPopupBlocked(false);
          // Don't clear productUrl - it should persist until reset
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => {
            setBigCommerceMessage('');
            setCartUrl('');
            setPopupBlocked(false);
            // Don't clear productUrl - it should persist until reset
          }}
          severity={bigCommerceSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
          action={bigCommerceSuccess && cartUrl && popupBlocked ? (
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                window.open(cartUrl, '_blank');
              }}
              variant="outlined"
              sx={{ 
                color: 'inherit', 
                borderColor: 'currentColor',
                '&:hover': { 
                  borderColor: 'currentColor',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Add to Cart
            </Button>
          ) : null}
        >
          {bigCommerceMessage}
        </Alert>
      </Snackbar>

      <BOMDialog
        open={bomDialogOpen}
        onClose={() => setBomDialogOpen(false)}
        config={config}
      />
    </Box>
  );
}

export default ReviewCart; 